import * as React from "react"
import {
  ClassificationSource,
  type EntityTransaction,
  type Transaction,
} from "@digits-graphql/frontend/graphql-bearer"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table"
import { TRANSACTION_ROW_CLASS } from "src/frontend/components/Shared/Transactions/types"
import { useTransactionsUpdates } from "src/frontend/components/Shared/Transactions/Updates/TransactionsUpdatesContext"
import { useTransactionRowClick } from "src/frontend/components/Shared/Transactions/useTransactionRowClick"

export function useTableRowSelection(transactions: Transaction[] | undefined) {
  const onRowClick = useTransactionRowClick()
  const { selectedTransactions, selectTransaction, transactionUpdates, unselectTransaction } =
    useTransactionsUpdates()

  const { estimateRowSize, getRowId } = useConstant(() => ({
    estimateRowSize: () => 55,
    getRowId: (row: EntityTransaction) => row.factId || "",
  }))

  const getRowClassName = React.useCallback(
    (row: EntityTransaction) =>
      stringHelper.classNames(
        TRANSACTION_ROW_CLASS,
        row.factId && transactionUpdates.has(row.factId) && "transaction-updated"
      ),
    [transactionUpdates]
  )

  const rowSelection = React.useMemo(() => {
    const selection: RowSelectionState = {}
    transactions?.reduce((sel, txn) => {
      if (txn.factId) {
        sel[txn.factId] = selectedTransactions.has(txn.factId)
      }
      return sel
    }, selection)
    return selection
  }, [selectedTransactions, transactions])

  const onRowSelectionChange = React.useCallback<OnChangeFn<RowSelectionState>>(
    (onChange) => {
      if (!transactions) return
      const newSelection = typeof onChange === "function" ? onChange(rowSelection) : onChange
      const transactionsMap = new Map(transactions.map((t) => [t.factId, t]))

      // Find transactions that were removed from selection
      for (const [factId, wasSelected] of Object.entries(rowSelection)) {
        if (wasSelected && !newSelection[factId]) {
          const transaction = transactionsMap.get(factId)
          if (transaction) {
            unselectTransaction(transaction)
          }
        }
      }

      // Find transactions that were added to selection
      for (const [factId, isSelected] of Object.entries(newSelection)) {
        if (isSelected && !rowSelection[factId]) {
          const transaction = transactionsMap.get(factId)
          if (transaction) {
            selectTransaction(transaction)
          }
        }
      }
    },
    [transactions, rowSelection, selectTransaction, unselectTransaction]
  )

  const rowSelectionDisabled = React.useMemo(
    () =>
      !transactions ||
      transactions.some((t) =>
        t.sources.some(({ name }) => name === ClassificationSource.QuickBooks)
      ),
    [transactions]
  )

  return {
    estimateRowSize,
    getRowId,
    getRowClassName,
    rowSelectionDisabled,
    rowSelection,
    onRowSelectionChange,
    onRowClick,
  }
}
